import React, {useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {Button} from "../../../../components/button/Button";
import {t} from "../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../CommonTranslationKey";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {CancelButton} from "../../../../components/button/CancelButton";
import DialogActions from "../../../../components/dialogs/DialogActions";
import TextField from "../../../../components/fields/textfield/TextField";
import DialogContent from "../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../components/dialogs/DialogTitle";
import Dialog from "../../../../components/dialogs/Dialog";
import Grid from "../../../../components/dialogs/Grid";
import Snackbar from "../snackbar/Snackbar";
import {UserGroupCreateDto} from "../../../../common/apis/usergroup/UserGroupCreateDto";
import {UserGroupDto} from "../../../../common/apis/usergroup/UserGroupDto";
import userGroupApiClient from "../../../../common/apis/usergroup/UserGroupApiClient";
import {UserGroupsTranslationKey} from "./UserGroupsTranslationKey";
import {invalidateFindAllUserGroupsQueryCache} from "./UserGroupQuery";
import {ValidationError} from "../../../../common/ValidationError";
import {UserGroupErrorCode} from "./UserGroupErrorCode";

interface UserGroupCreateDialogProps {
    onClosed: () => void,
    onGroupCreated: (group: UserGroupDto) => void,
}

export default function UserGroupCreateDialog(props: Readonly<UserGroupCreateDialogProps>) {
    const {onClosed, onGroupCreated} = props;
    const queryClient = useQueryClient();

    const [name, setName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [nameError, setNameError] = useState<string>();

    function validateName(): boolean {
        if (name.trim().length === 0) {
            setNameError(t(CommonTranslationKey.FILL_OUT_THIS_FIELD));
            return false;
        }
        setNameError(undefined);
        return true;
    }

    function saveChanges() {
        if (validateName()) {
            const createDto: UserGroupCreateDto = {
                name: name.trim(),
                description: description.trim() || undefined,
            };
            userGroupApiClient.create(createDto)
                .then((createdGroup) => {
                    invalidateFindAllUserGroupsQueryCache(queryClient);
                    Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                    onGroupCreated(createdGroup);
                })
                .catch((err) => {
                    if (err instanceof ValidationError && err.error.code === UserGroupErrorCode.NAME_ALREADY_EXISTS) {
                        setNameError(t(UserGroupsTranslationKey.USER_GROUP_NAME_ALREADY_EXISTS));
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), err);
                    }
                });
        }
    }

    return (
        <Dialog open={true} onClose={onClosed}>
            <DialogTitle title={t(UserGroupsTranslationKey.NEW_USER_GROUP)}
                         onDialogClosed={onClosed}
            />
            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <TextField id="user-group-create-name-field"
                                   label={t(UserGroupsTranslationKey.NAME)}
                                   value={name}
                                   errorMessage={nameError}
                                   onChange={(newName) => setName(newName)}
                                   required={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField id="user-group-create-description-field"
                                   label={t(UserGroupsTranslationKey.DESCRIPTION)}
                                   value={description}
                                   onChange={(newDesc) => setDescription(newDesc)}
                                   multiline={true}
                                   rows={4}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClosed}/>
                <Button label={t(CommonTranslationKey.CREATE)} variant="contained" onClick={saveChanges}/>
            </DialogActions>
        </Dialog>
    );
}
